import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGoogle } from "@/assets/icons/geist/IconGoogle";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { IconSandglass } from "@/assets/icons/geist/IconSandglass";
import { IconSSO } from "@/assets/icons/geist/IconSSO";
import { Button } from "@/components/flexkit/Button";
import { Alert } from "@/components/podkit/alert/Alert";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { TrackLocations } from "@/hooks/use-segment";
import { useGetDomainVerification, useUpdateSSOConfiguration, useVerifyDomain } from "@/queries/organization-queries";
import { SetupSSOModal, type SetupSSOModalStep } from "@/routes/settings/login/SetupSSOModal";
import { formatError } from "@/utils/errors";
import * as Switch from "@radix-ui/react-switch";
import {
    DomainVerificationState,
    SSOConfigurationState,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState, type FC, type ReactNode } from "react";

type BaseCardProps = {
    title: string;
    icon?: JSX.Element;
    // description is rendered bellow the title and icon
    description?: ReactNode;
    // actions are rendered on the right side of the card
    actions?: ReactNode;
    // banner is rendered at the bottom of the card
    banner?: ReactNode;
};

const BaseCard: FC<BaseCardProps> = ({ title, icon, description, actions, banner }) => (
    <div className="flex min-h-[80px] w-[550px] flex-col gap-4 rounded-xl border border-border-base px-5 py-4">
        <div className="flex grow flex-row gap-4">
            <div className="flex items-center">{icon || <IconSSO className="size-10 text-content-secondary" />}</div>
            <div className="flex grow flex-col justify-center">
                <Heading2>{title}</Heading2>
                {description}
            </div>
            {actions}
        </div>
        {banner}
    </div>
);

type SSOConfigCardProps = {
    organizationId: string;
    config?: SSOConfiguration;
    deactivateable: boolean;
};

export const CustomSSOConfigCard: FC<SSOConfigCardProps> = ({ organizationId, config, deactivateable }) => {
    const { toast } = useToast();
    const [showSetupSSO, setShowSetupSSO] = useState<SetupSSOModalStep | undefined>(undefined);

    const { domainVerification } = useGetDomainVerification(config?.emailDomain);
    const domain = domainVerification?.domain || "";
    const isDomainVerified = domainVerification?.state === DomainVerificationState.VERIFIED;

    const verifyDomain = useVerifyDomain();
    const handleVerifyDomain = useCallback(async () => {
        if (!domainVerification) {
            return;
        }
        try {
            const result = await verifyDomain.mutateAsync(domainVerification);
            const title =
                result?.state === DomainVerificationState.VERIFIED
                    ? `${domain} is verified.`
                    : `${domain} is not yet verified. Please try again later.`;

            toast({
                title,
            });
        } catch (error) {
            toast({
                title: "Failed to verify domain.",
                description: formatError(error),
            });
        }
    }, [domain, domainVerification, toast, verifyDomain]);

    const description = domain && (
        <span className="flex flex-row items-center gap-1 pt-1 text-base text-content-secondary">
            {isDomainVerified ? (
                <>
                    <IconCheckFill className="text-content-green" size="base" /> {domain}
                </>
            ) : (
                <Tooltip content="The Login with SSO will be available after the domain is verified.">
                    <span className="flex flex-row items-center gap-1">
                        <IconSandglass /> {domain} pending
                    </span>
                </Tooltip>
            )}
        </span>
    );

    const banner = domain && !isDomainVerified && (
        <Alert className="flex flex-row items-center gap-4 border-0 bg-surface-02 p-3">
            <Text className="text-base text-content-secondary">
                Waiting to verify your domain. Click to retry the verification.
            </Text>
            <div className="grow" />
            <Button
                disabled={!domainVerification}
                onClick={handleVerifyDomain}
                loading={verifyDomain.isPending}
                variant={"secondary"}
                size={"md"}
                LeadingIcon={IconRefresh}
            />
        </Alert>
    );

    const title = "Single Sign On";
    const actions = (
        <div className="flex items-center justify-end gap-4">
            {!config && (
                <Button variant={"primary"} size={"md"} onClick={() => setShowSetupSSO("configure-sso")}>
                    Set up
                </Button>
            )}
            {config && !isDomainVerified && (
                <>
                    <Button variant={"secondary"} size={"md"} onClick={() => setShowSetupSSO("configure-sso")}>
                        Edit
                    </Button>
                    <Button variant={"primary"} size={"md"} onClick={() => setShowSetupSSO("domain-verification")}>
                        Verify
                    </Button>
                </>
            )}
            {config && isDomainVerified && (
                <>
                    <Button variant={"secondary"} size={"md"} onClick={() => setShowSetupSSO("configure-sso")}>
                        Edit
                    </Button>
                    <EnablementToggle title={title} config={config} deactivateable={deactivateable} />
                </>
            )}
        </div>
    );

    return (
        <div>
            <BaseCard title={title} actions={actions} description={description} banner={banner} />
            {showSetupSSO && (
                <SetupSSOModal
                    organizationId={organizationId}
                    config={config}
                    step={showSetupSSO}
                    onClose={() => {
                        setShowSetupSSO(undefined);
                    }}
                />
            )}
        </div>
    );
};

export const BuiltinSSOConfigCard: FC<SSOConfigCardProps> = ({ config, deactivateable }) => {
    const title = config ? getTitle(config) : "";

    const icon = config ? getIcon(config) : undefined;
    const actions = config && (
        <EnablementToggle title={title || getTitle(config)} config={config} deactivateable={deactivateable} />
    );

    return <BaseCard title={title} icon={icon} actions={actions} />;
};

const getTitle = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return "GitHub";
    }
    if (config.issuerUrl?.includes("google")) {
        return "Google";
    }
    return "Unknown";
};

const getIcon = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return <IconGitHub size="lg" className="size-10" />;
    }
    if (config.issuerUrl?.includes("google")) {
        return <IconGoogle size="lg" className="size-10" />;
    }
    return undefined;
};

const EnablementToggle: FC<{ config: SSOConfiguration; title: string; deactivateable: boolean }> = ({
    config: { id, state },
    title,
    deactivateable,
}) => {
    const updateSSOConfig = useUpdateSSOConfiguration();
    const { toast } = useToast();

    const [showModal, setShowModal] = useState(false);

    const isActive = state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE;

    const updateState = useCallback(
        async (state: SSOConfigurationState) => {
            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: id,
                        state,
                    }),
                );
            } catch (error) {
                toast({
                    title: "Failed to update SSO configuration",
                    description: formatError(error),
                });
            }
        },
        [id, toast, updateSSOConfig],
    );

    const handleCheckedChange = useCallback(async () => {
        if (!isActive) {
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE);
        } else {
            setShowModal(true);
        }
    }, [isActive, updateState]);

    const handleSubmitDeactivation = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_INACTIVE);
            setShowModal(false);
        },
        [updateState],
    );

    return (
        <>
            <Tooltip content={!deactivateable && isActive ? "You can't deactivate the single remaining provider." : ""}>
                <div className="flex items-center">
                    <Switch.Root
                        checked={isActive}
                        disabled={!deactivateable && isActive}
                        onCheckedChange={handleCheckedChange}
                        className={
                            "relative h-[25px] w-[42px] cursor-pointer rounded-full bg-surface-04 outline-none disabled:cursor-not-allowed data-[state=checked]:bg-content-green disabled:data-[state=checked]:bg-content-green/50"
                        }
                        id={"toggle-" + id}
                    >
                        <Switch.Thumb
                            className={
                                "flex size-5 translate-x-0.5 items-center justify-center gap-2 rounded-full bg-surface-pure shadow-black/10 transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[19px]"
                            }
                        />
                    </Switch.Root>
                </div>
            </Tooltip>
            {showModal && (
                <Dialog open onOpenChange={setShowModal}>
                    <DialogContent
                        className="max-w-[600px]"
                        data-testid="deactivate-form-modal-content"
                        data-track-location={TrackLocations.DeactivateSSOModal}
                    >
                        <DialogHeader>
                            <DialogTitle>Deactivating {title} log in </DialogTitle>
                            <DialogDescription />
                        </DialogHeader>

                        <DialogBody className="overflow-x max-w-full">
                            <form
                                id="deactivate-form"
                                onSubmit={handleSubmitDeactivation}
                                className="flex flex-col gap-4"
                            >
                                <Text className="text-base">Users will no longer be able to log in with {title}.</Text>
                                <Text className="text-base">
                                    They will need to create a new account using one of the other available log in
                                    methods.
                                </Text>
                            </form>
                        </DialogBody>

                        <DialogFooter className="sm:justify-end">
                            <DialogClose asChild>
                                <Button type="button" variant="secondary" autoFocus={true}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                variant="destructive"
                                form="deactivate-form"
                                loading={updateSSOConfig.isPending}
                                disabled={updateSSOConfig.isPending}
                                data-testid="deactivate-submit-button"
                            >
                                Deactivate
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
